import type { BadgeTone } from '@hrms/ui';
import type { TrainingTaskStatus } from '@hrms/shared';

/** HR's words for a course's stored status (R1: "Published", never "active"). */
export const COURSE_STATUS_LABEL: Record<TrainingTaskStatus, string> = {
  draft: 'Draft',
  active: 'Published',
  archived: 'Archived',
};

export const COURSE_STATUS_TONE: Record<TrainingTaskStatus, BadgeTone> = {
  draft: 'neutral',
  active: 'success',
  archived: 'warning',
};

export type CourseTab = 'content' | 'learners' | 'settings';

/** `/admin/training/courses/:taskId` — the course page (R2), optionally on a tab. */
export function coursePagePath(taskId: string, tab?: CourseTab): string {
  return `/admin/training/courses/${taskId}${tab && tab !== 'content' ? `?tab=${tab}` : ''}`;
}

export const NEW_COURSE_PATH = '/admin/training/courses/new';
export const COURSES_PATH = '/admin/training/courses';

/**
 * One row per course. The library list returns every VERSION (an edit to an assigned course forks
 * a new row and archives the old one), so among the rows loaded keep the newest version of each
 * lineage, in the order the server sent them.
 */
export function latestVersionPerCourse<T extends { lineageId: string; version: number }>(
  rows: readonly T[],
): T[] {
  const newest = new Map<string, T>();
  for (const row of rows) {
    const current = newest.get(row.lineageId);
    if (!current || row.version > current.version) newest.set(row.lineageId, row);
  }
  return rows.filter((row) => newest.get(row.lineageId) === row);
}

/** The id of the newest version of `task`'s course among `rows`, when it is newer than `task` —
 * where an old version's link should land instead (B12). */
export function newerVersionId(
  rows: readonly { id: string; lineageId: string; version: number }[],
  task: { lineageId: string; version: number },
): string | null {
  let newest: { id: string; version: number } | null = null;
  for (const row of rows) {
    if (row.lineageId !== task.lineageId || row.version <= task.version) continue;
    if (!newest || row.version > newest.version) newest = row;
  }
  return newest?.id ?? null;
}

/** "81% completed" — of the people who have the course, how many finished it. */
export function completionLabel(assignedCount: number, completedCount: number): string | null {
  if (assignedCount <= 0) return null;
  return `${Math.round((Math.min(completedCount, assignedCount) / assignedCount) * 100)}% completed`;
}
